import { useTheme } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { MdBlock, MdCheckCircleOutline, MdKeyboardArrowDown, MdOutlineInsertDriveFile } from "react-icons/md";

import { ticketStatuses, TicketStatus } from "@megaron/support-contracts";

type Props = {
  value: TicketStatus;
  isDisabled?: boolean;
  onChange: (status: TicketStatus) => void;
};

const statusLabels: Record<TicketStatus, string> = {
  open: "Otwarty",
  resolved: "Zrealizowany",
  closed: "Zamknięty",
};

const statusColor = (status: TicketStatus, colors: { primary: string; success: string; warning: string; danger: string }) =>
  status === "resolved" ? colors.success : status === "closed" ? colors.danger : colors.primary;

const statusIcon = (status: TicketStatus) =>
  status === "resolved" ? (
    <MdCheckCircleOutline size={18} />
  ) : status === "closed" ? (
    <MdBlock size={18} />
  ) : (
    <MdOutlineInsertDriveFile size={18} />
  );

const statusOptions: TicketStatus[] = [...ticketStatuses];

export const SupportStatusSelect: React.FC<Props> = ({ value, onChange, isDisabled }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const path = event.composedPath();
      const root = dropdownRef.current;
      if (root && !path.includes(root)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} css={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
        css={{
          borderRadius: theme.smallBorderRadius,
          display: "flex",
          padding: "10px 12px",
          justifyContent: "space-between",
          border: `1px solid ${theme.colors.border}`,
          alignItems: "center",
          fontSize: "14px",
          fontWeight: 700,
          cursor: isDisabled ? "not-allowed" : "pointer",
          width: "100%",
          gap: "8px",
          background: "white",
          opacity: isDisabled ? 0.7 : 1,
        }}
      >
        <span css={{ color: statusColor(value, theme.colors), display: "flex", gap: "8px", alignItems: "center" }}>
          {statusIcon(value)}
          {statusLabels[value]}
        </span>
        <MdKeyboardArrowDown size={18} color={theme.colors.secondaryText} />
      </button>
      {isOpen && !isDisabled && (
        <div
          css={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            width: "100%",
            borderRadius: theme.smallBorderRadius,
            boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.2)",
            padding: "0.25rem 0",
            backgroundColor: "white",
            zIndex: 2,
          }}
        >
          {statusOptions.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => {
                onChange(status);
                setIsOpen(false);
              }}
              css={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                cursor: "pointer",
                border: "none",
                background: "transparent",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: statusColor(status, theme.colors),
                fontWeight: 600,
                "&:hover": { background: theme.colors.hover },
              }}
            >
              {statusIcon(status)}
              {statusLabels[status]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
